/**
 * Dialog shell — the Create WORKSPACE (A+B->C), one persistent layer.
 *
 * Layout inside #mode-create: a 2-column `.cw-shell`:
 *   LEFT  `.cw-tools`  — actions (intent picker, grouped) + A source / B reference
 *                        slots + an optional note + model/aspect + Generate.
 *   RIGHT `.cw-canvas` — C result + the "Put on a product" funnel CTA + mockup tiles.
 * Inspiration (the image's vision-extracted prompt cards) live in the docked
 * right-panel (`[data-rp-panel]`), rendered by the inspiration module — not here.
 *
 * Element IDs are kept stable (#promptInput / #generateBtn / #dialogSource /
 * #dialogReference / #dialogResult* / #mockupTilesRow / #createFileInput /
 * #createLightbox) so the generate / result / image-role wiring re-binds with no
 * change. why → A+B->C workspace (bao 2026-06-23; R shell + image-is-the-prompt).
 *
 * This file is the PUBLIC ENTRY: it holds the shell lifecycle + generate wiring
 * (_container/_generateHandler state) and re-exports the split-out pieces —
 * scaffold HTML (shell-scaffold.js), A/B slots (image-slots.js), C result panel
 * (result-panel.js), event bindings + lightbox (shell-bindings.js).
 *
 * Public surface:
 *   initDialogShell(container) · activateShell() · deactivateShell()
 *   onGenerateClick(handler) · renderPlays(intents)
 *   showSourceImage / clearSourceImage / showReferenceImage
 *   showResultImage / setResultLoading / setResultError / resetResultSlot
 */

import { store } from '../../../core/store.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { resolveInputMode } from './form-modes.js'
import { clearReferenceState } from '../../create-ui/image-roles.js'
import { shellScaffoldHTML, fillAspectDropdown, escHtml, escAttr } from './shell-scaffold.js'
import { bindShellOnceListeners, bindWorkspace } from './shell-bindings.js'
import { showSourceImage, clearSourceImage, showReferenceImage, externalCardsNeeded } from './image-slots.js'
import { showResultImage, resetResultSlot, setResultLoading, showResult } from './result-panel.js'
import { initStyleGrid } from './style-grid.js'
import { createLogger } from '../../../utils/logger.js'
const log = createLogger('create.dialog.shell')

let _container = null
let _generateHandler = null
// Warn once about undispatchable cards (data problem, not a per-render event).
let _warnedNoExecMode = false

/**
 * Render the action (intent) picker into the left toolbar, grouped by the intent's
 * pipeline metadata (`input_mode` + `requires_second_image`) so the grouping is a
 * projection of the real dispatch pipeline, not a hand-coded list. A tile click sets
 * `creation.intentCard` and reveals/hides the B (reference) slot per the contract.
 * why → action = intent card; the frontend never branches by backend model (ADR-005 2.6.0).
 *
 * @param {Array<Object>} cards - intent-role cards (Card[] with execution_mode etc.)
 */
export function renderPlays(cards) {
  const wrap = _container?.querySelector('#cwPlays')
  if (!wrap) return
  // Gate intents whose backend isn't wired: FASHN-class try-on / model-swap /
  // ghost-mannequin need a specialized fashion model we don't run (generic kontext
  // does them poorly), and they're off the POD core ("put your design on a
  // product"). Drop this filter once the FASHN backend lands. why → bao 2026-06-25.
  // Style Transfer is KEPT (2026-06-25): the unstable path was TWO-image style
  // transfer (A content + B reference → kontext-multi produced unrelated output).
  // Single-image stylization (A + text style, e.g. "oil painting") is a reliable
  // kontext img2img edit — judge-verified PASS for oil + watercolour. The card is
  // flipped to requires_second_image=0 (migration) so it runs the single-image
  // path; a B style-reference image is Phase 2. why → bao 2026-06-25.
  const UNAVAILABLE = /try-?on|model\s*swap|ghost\s*mannequin/i
  // execution_modes with no backend surface yet (matrix-alignment.test.ts
  // KNOWN_UNWIRED): the card would render, dispatch to /generate, and 400.
  // why → keep plays clickable = playable.
  const KNOWN_UNWIRED = new Set(['pattern', 'ideogram_remix'])
  const all = (Array.isArray(cards) ? cards : []).filter(c => !UNAVAILABLE.test(c.title || '') && !KNOWN_UNWIRED.has(c.execution_mode))
  // A card without execution_mode cannot be dispatched (getDispatchConfig throws
  // InvalidExecutionModeError → every pick would end in "Generation failed").
  // Drop them from the plays, but warn once so the data problem stays visible.
  const undispatchable = all.filter(c => !c.execution_mode)
  if (undispatchable.length && !_warnedNoExecMode) {
    _warnedNoExecMode = true
    log.warn('intent cards without execution_mode hidden from plays:', undispatchable.map(c => c.id))
  }
  const list = all.filter(c => c.execution_mode)
  // Group: edit-one (image, !req2nd), combine-two (req2nd=1), from-text/tools.
  const groups = [
    { key: 'edit', label: 'Edit this image', items: list.filter(c => c.input_mode !== 'text' && !c.requires_second_image) },
    { key: 'combine', label: 'Combine two images · needs B', items: list.filter(c => c.requires_second_image) },
    { key: 'text', label: 'From text / Tools', items: list.filter(c => c.input_mode === 'text' && !c.requires_second_image) },
  ].filter(g => g.items.length)

  const active = store.getState('creation.intentCard')?.id
  wrap.innerHTML = groups.map(g => `
    <div class="cw-glabel">${escHtml(g.label)}</div>
    <div class="cw-chips">${g.items.map(c => `
      <button class="cw-chip${c.id === active ? ' on' : ''}" type="button" data-intent="${escAttr(String(c.id))}" title="${escAttr(c.description || c.title || '')}">
        <i class="ph ${intentIcon(c)}" aria-hidden="true"></i><span>${escHtml(c.title || '')}</span>
      </button>`).join('')}</div>`).join('')

  wrap.querySelectorAll('.cw-chip').forEach(el => el.addEventListener('click', () => {
    const card = list.find(c => String(c.id) === el.dataset.intent)
    if (!card) return
    const cur = store.getState('creation.intentCard')
    if (cur?.id === card.id) {
      store.setState('creation.intentCard', null)
      eventBus.emit(EVENTS.INTENT_FILTER, { defaultOnCategories: null })
    } else {
      store.setState('creation.intentCard', card)
      // Reveal B only when this intent needs a second pixel. why → ADR-005 #4/#10.
      clearReferenceState()
      // Filter the inspiration cards to this intent's relevant categories
      // (lowercased to match the inspiration workspace's catKey). why → Zeus F3.
      const cats = card.default_on_categories
        ? card.default_on_categories.split(',').map(s => s.trim().toLowerCase())
        : null
      eventBus.emit(EVENTS.INTENT_FILTER, { defaultOnCategories: cats })
    }
    renderPlays(list)
  }))
}

// Phosphor icon per intent — derived from the title (the DB cards carry no icon).
function intentIcon(card) {
  const t = (card.title || '').toLowerCase()
  if (t.includes('color')) return 'ph-palette'
  if (t.includes('background')) return 'ph-image'
  if (t.includes('remove')) return 'ph-eraser'
  if (t.includes('light')) return 'ph-lightbulb'
  if (t.includes('enhance')) return 'ph-sparkle'
  if (t.includes('style')) return 'ph-magic-wand'
  if (t.includes('try') || t.includes('product')) return 'ph-t-shirt'
  if (t.includes('model')) return 'ph-user-switch'
  if (t.includes('text') || t.includes('print')) return 'ph-text-aa'
  if (t.includes('logo') || t.includes('vector')) return 'ph-bounding-box'
  if (t.includes('upscale')) return 'ph-arrows-out'
  return 'ph-magic-wand'
}

// A + B -> C is ALWAYS drawn — one universal frontend layout, never hides slots by
// backend model. B is always an optional input; the backend takes its value only
// when the chosen pipeline consumes it (gated in buildJobInput). why → unified
// A+B->C, frontend never branches by model (bao 2026-06-24).

// ---- Public lifecycle ----

/**
 * Mount the workspace scaffold into `container`, bind all listeners, and start
 * the `creation.result` store subscription.
 * @param {HTMLElement} container - #mode-create host element
 */
export function initDialogShell(container) {
  _container = container
  container.innerHTML = shellScaffoldHTML()
  fillAspectDropdown(container)
  bindShellOnceListeners(container)
  bindWorkspace(container)
  initStyleGrid(container)

  store.subscribe('creation.result', () => showResult())
  showResult()
  rebindGenerate()
}

/**
 * Re-hydrate the A and C slots from store state when the create mode activates.
 */
export function activateShell() {
  const ref = store.getState('sourceImage')
  if (ref?.url) {
    // An injected/external source (?injectImage — a URL with no typed slot ref)
    // never went through the IMAGE_SELECT pick path, so its cards were never
    // read. Read them once here so the funnel entry gets the same "image IS the
    // prompt" treatment as a library pick. why → ADR-005 2.7.0 storefront funnel.
    showSourceImage(ref.url, externalCardsNeeded(ref), ref.id || null)
  }
  const result = store.getState('creation.result')
  // Re-pass the full result record — repainting with the URL alone used to wipe
  // generation_id / card_ids / asset_id from the store on every mode switch.
  if (result?.url) showResultImage(result.url, result.generation_id || null, result.card_ids || [], result.asset_id || null)
}

/**
 * Close transient UI (model/aspect dropdowns) when the create mode deactivates.
 */
export function deactivateShell() {
  document.getElementById('modelDropdown')?.classList.add('hidden')
  document.getElementById('aspectDropdown')?.classList.add('hidden')
}

/**
 * Register the Generate-button click handler.
 * @param {() => void} handler
 */
export function onGenerateClick(handler) {
  _generateHandler = handler
  rebindGenerate()
}

function rebindGenerate() {
  const btn = _container?.querySelector('#generateBtn')
  if (!btn) return
  const fresh = btn.cloneNode(true)
  btn.parentNode.replaceChild(fresh, btn)
  if (_generateHandler) fresh.addEventListener('click', _generateHandler)
}

// ---- Result error (C) — lives here: its Retry re-fires _generateHandler ----

/**
 * Put the C slot into its error state with a Retry button that re-fires the
 * registered generate handler.
 * @param {string} message
 */
export function setResultError(message) {
  const ph = document.getElementById('dialogResultPh')
  if (ph) {
    ph.style.display = ''
    ph.innerHTML = `<i class="ph ph-warning-circle" style="color:var(--state-danger)"></i><span style="color:var(--state-danger)">${escHtml(message)}</span><button type="button" class="cw-retry" id="cwRetry">Retry</button>`
    ph.querySelector('#cwRetry')?.addEventListener('click', () => { if (_generateHandler) _generateHandler() })
  }
  store.setState('creation.result', null)
}

// ---- Re-exports (public entry — external import paths stay unchanged) ----

export { showSourceImage, clearSourceImage, showReferenceImage }
export { showResultImage, resetResultSlot, setResultLoading }
export { resolveInputMode }
